import React from 'react'

import * as yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormHelperText } from '@mui/material'
import { Clear as ClearIcon } from '@mui/icons-material'

import { localizedStrings } from '@core/strings'
import { ParticipantType } from '@core/modules/briefings'
import { PermitTemplateRole } from '@core/entities/permit/PermitTemplateRoles'
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@common/material'
import { Box } from '@fundamentals'
import { ModalDialogContainer } from '@common/modal'
import { useModal } from '@common/GlobalModal'

type FormData = Pick<
  ParticipantType,
  'roleId' | 'firstName' | 'lastName' | 'company'
>

type BriefingAddExternalUserModalProps = {
  roles: Array<Pick<PermitTemplateRole, 'id' | 'name'>>
  onAddExternalUser: (data: FormData) => void
}

const schema = yup.object().shape<FormData>({
  roleId: yup.number().required(localizedStrings.roleRequired),
  firstName: yup.string().required(localizedStrings.firstNameRequired),
  lastName: yup.string().required(localizedStrings.lastNameRequired),
  company: yup.string().required(localizedStrings.companyRequired),
})

export const BriefingAddExternalUserModal: React.FC<
  BriefingAddExternalUserModalProps
> = ({ roles, onAddExternalUser }) => {
  const { hideModal } = useModal()

  const { control, handleSubmit } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      roleId: undefined,
      firstName: '',
      lastName: '',
      company: '',
    },
  })

  const onSubmit = (data: FormData) => {
    onAddExternalUser(data)
    hideModal()
  }

  return (
    <ModalDialogContainer>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle
          variant='h5'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{ pr: 1, fontSize: 20, mb: 0 }}
        >
          {localizedStrings.addExternalUser}
          <IconButton aria-label='close-modal' onClick={hideModal}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 4 }}>
            <Controller
              name='roleId'
              control={control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth required error={!!fieldState.error}>
                  <InputLabel>{localizedStrings.role}</InputLabel>
                  <Select {...field} label={localizedStrings.role}>
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <FormHelperText error>
                      {fieldState.error.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name='firstName'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={localizedStrings.firstName}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />

            <Controller
              name='lastName'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={localizedStrings.lastName}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />

            <Controller
              name='company'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={localizedStrings.company}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ pt: 2, pb: 2, pl: 3, pr: 3 }}>
          <Button variant='outlined' onClick={hideModal} type='button'>
            {localizedStrings.cancel}
          </Button>
          <Button variant='contained' color='primary' type='submit'>
            {localizedStrings.confirm}
          </Button>
        </DialogActions>
      </form>
    </ModalDialogContainer>
  )
}
